import {
  Badge,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  CardTitle,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Table,
} from 'reactstrap';
import { ProperAssignedVisit } from '../models/dashboardModels';
import {
  IconCalendar,
  IconCheck,
  IconClock,
  IconDots,
  IconMail,
  IconMapPin,
  IconPhone,
  IconTrash,
} from '@tabler/icons-react';
import {
  getBadgeColorVisitStatusAdapter,
  getVisitStatusAdapter,
} from '../../properties/adapters/visitsAdapter';
import { VISIT_STATUS } from '../../../constants/visitsConstants';
import { useCallback, useState } from 'react';
import VisitMarketplaceTypeBadge from '../../../components/VisitMarketplaceTypeBadge';
import useCallApiAndLoad from '../../../hooks/useCallApiAndLoad';
import useAuth from '../../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { updateVisitStateEndpoint } from '../services/dashboardService';
import {
  deleteVisitEndpoint,
  getCancellationReasons,
} from '../../properties/services/propertyService';
import DeleteVisitModal from '../../properties/components/DeleteVisitModal';
import RentSecondaryVisitBadge from '../../../components/badges/RentSecondaryVisitBadge';

export interface Props {
  visits: ProperAssignedVisit[];
  footer?: any;
}
const DashboardVisitListTable = ({ visits, footer }: Props) => {
  const [dropdownOpen, setDropdownOpen] = useState<{ [key: string]: boolean }>(
    {},
  );
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [cancellationReasons, setCancellationReasons] = useState<string[]>([]);
  const [selectedVisitId, setSelectedVisitId] = useState<string | null>(null);

  const toggleDropdown = (visitId: string) => {
    setDropdownOpen((prev) => ({
      ...prev,
      [visitId]: !prev[visitId],
    }));
  };

  const { callEndpoint } = useCallApiAndLoad();
  const { getAuthToken } = useAuth();
  const token = getAuthToken();

  const navigate = useNavigate();

  const doVisitDone = useCallback(
    async (visitId: string) => {
      const { status } = await callEndpoint(
        updateVisitStateEndpoint(visitId, true, token),
      );
      if (status === 200) navigate(0);
    },
    [callEndpoint, token],
  );

  const doCancelVisit = useCallback(
    async (visitId: string) => {
      const { status } = await callEndpoint(
        deleteVisitEndpoint(visitId, token),
      );
      if (status === 200) navigate(0);
    },
    [callEndpoint, token],
  );

  const handleOpenDeleteModal = async (visitId: string) => {
    setSelectedVisitId(visitId);
    try {
      const { data } = await callEndpoint(getCancellationReasons(token));
      setCancellationReasons(Array.isArray(data.data) ? data.data : []);
    } catch {
      setCancellationReasons([]);
    }
    setDeleteModalOpen(true);
  };

  const handleDeleteVisit = async (reason: string, explain_reason: string) => {
    if (!selectedVisitId) return;
    const { status } = await callEndpoint(
      deleteVisitEndpoint(selectedVisitId, token, reason, explain_reason),
    );
    if (status === 200) navigate(0);
    setDeleteModalOpen(false);
    setSelectedVisitId(null);
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Visitas</CardTitle>
        </CardHeader>
        <CardBody>
          <div className="table-responsive">
            <Table striped hover>
              <thead>
                <tr>
                  <th>Propiedad</th>
                  <th>Inquilino</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {visits.map((visit) => (
                  <tr key={visit.id}>
                    <td>
                      <div>
                        <div className="font-weight-bold">
                          [{visit.propertyInfo.code}] {visit.propertyInfo.name}
                        </div>
                        <div className="d-flex align-items-center text-muted small">
                          <IconMapPin size={12} className="me-1" />
                          {visit.propertyInfo.address}
                        </div>
                        <div className="text-success font-weight-bold">
                          {visit.propertyInfo.price}
                        </div>
                        <div>
                          <VisitMarketplaceTypeBadge
                            marketplace_type={visit.visitInfo.marketplace_type}
                          />
                        </div>
                      </div>
                    </td>
                    <td>
                      <div>
                        <div className="font-weight-bold">
                          {visit.visitInfo.tenant_name}
                        </div>
                        <div className="d-flex align-items-center text-muted">
                          <IconMail size={12} className="me-1" />{' '}
                          {visit.visitInfo.tenant_email}
                        </div>
                        <div className="d-flex align-items-center text-muted">
                          <IconPhone size={12} className="me-1" />
                          {visit.visitInfo.tenant_phone}
                        </div>
                        <div className="d-flex align-items-center font-weight-bold">
                          <IconCalendar size={12} className="me-1" />
                          {visit.visitInfo.visit_date}
                        </div>
                        <div className="d-flex align-items-center text-muted">
                          <IconClock size={12} className="me-1" />
                          {visit.visitInfo.visit_time}
                        </div>
                      </div>
                    </td>
                    <td>
                      <RentSecondaryVisitBadge
                        status={visit.visitInfo.status}
                        className="badge-lg mr-2"
                      />
                    </td>
                    <td>
                      <Dropdown
                        isOpen={dropdownOpen[visit.id] || false}
                        toggle={() => toggleDropdown(visit.id)}
                      >
                        <DropdownToggle
                          tag="button"
                          className="btn btn-sm btn-outline-secondary"
                          caret={false}
                        >
                          <IconDots size={16} />
                        </DropdownToggle>
                        <DropdownMenu>
                          <DropdownItem
                            className="text-success"
                            onClick={() => doVisitDone(visit.id)}
                          >
                            <IconCheck size={16} className="me-2" />
                            Marcar como realizada
                          </DropdownItem>
                          <DropdownItem>
                            <IconCalendar size={16} className="me-2" />
                            Ver en calendario
                          </DropdownItem>
                          <DropdownItem divider />
                          <DropdownItem
                            className="text-danger"
                            onClick={() => handleOpenDeleteModal(visit.id)}
                          >
                            <IconTrash size={16} className="me-2" />
                            Cancelar visita
                          </DropdownItem>
                        </DropdownMenu>
                      </Dropdown>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </CardBody>
        <CardFooter>{footer && <div>{footer}</div>}</CardFooter>
      </Card>
      <DeleteVisitModal
        isOpen={deleteModalOpen}
        toggle={() => setDeleteModalOpen(false)}
        onConfirm={handleDeleteVisit}
        reasons={cancellationReasons}
      />
    </>
  );
};

export default DashboardVisitListTable;
